import { Button, Card, HSpacer, Text, VSpacer } from '@/components/DesignSystem';
import { ApiPromotion } from '@api/interfaces/ApiPromotion';
import LocalOffer from '@mui/icons-material/LocalOffer';
import { Stack, useTheme } from '@mui/material';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ProductExclusionsModal } from './ProductExclusionsModal';
import { ProductPromotionsModal } from './ProductPromotionsModal';

type ProductListData = {
  externalId: string | null,
  id: string,
  manufacturerName?: string,
  name: string
}

interface ProductListItemProps {
  canManageExclusions?: boolean,
  productListData: ProductListData,
  promotions: ApiPromotion[],
}

const ProductListItem = ({
  canManageExclusions,
  productListData,
  promotions,
}: ProductListItemProps) => {
  const { t } = useTranslation();
  const { externalId, id, manufacturerName, name } = productListData;
  const [showViewProductPromotionsModal, setShowViewProductPromotionsModal] = useState(false);
  const [showExclusionsModal, setShowExclusionsModal] = useState(false);
  const theme = useTheme();

  return (
    <>
      <Card elevation={4} testID={`product-list-item-${id}`}>
        <Stack direction="row" justifyContent="space-between">
          <Stack>
            {!!manufacturerName && (
              <>
                <Text
                  category="overline"
                  sx={{ color: theme.palette.text.secondary }}
                  testID="product-manufacturer-name"
                >
                  {manufacturerName.toUpperCase()}
                </Text>
                <VSpacer size="1" />
              </>
            )}
            <Text category="body-xlarge" testID="product-name" >
              {name}
            </Text>
            <VSpacer size="1" />
            <Text
              category="body-medium"
              sx={{ color: theme.palette.text.secondary }}
              testID="product-id"
            >
              {externalId
                ? t($ => $.products.erpId, { id: externalId })
                : t($ => $.products.id, { id })}
            </Text>
          </Stack>
          <Stack alignItems="center" direction="row" gap="24px">
            {canManageExclusions && (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowExclusionsModal(true);
                }}
                size="small"
                testID="manage-exclusions-button"
                variant="outlined"
              >
                {t($ => $.products.exclusions.manage)}
              </Button>
            )}
            {!!promotions.length && (
              <Button
                onClick={(e) => {
                  e.stopPropagation();
                  setShowViewProductPromotionsModal(true);
                }}
                size="small"
                sx={{
                  backgroundColor: theme.palette.rewardsYellow,
                  borderRadius: '8px',
                  '&:hover': {
                    backgroundColor: theme.palette.rewardsYellow,
                  },
                }}
                testID="rewards-button"
              >
                <LocalOffer
                  sx={{ color: theme.palette.text.primary, height: '18px', width: '18px' }}
                />
                <HSpacer size="2"/>
                <Text category="label-large" sx={{ color: theme.palette.text.primary }}>
                  {t($ => $.products.viewPromotions)}
                </Text>
              </Button>
            )}
          </Stack>
        </Stack>
      </Card>
      {showViewProductPromotionsModal && (
        <ProductPromotionsModal
          onClose={() => setShowViewProductPromotionsModal(false)}
          promotions={promotions}
        />
      )}
      {showExclusionsModal && (
        <ProductExclusionsModal
          onClose={() => setShowExclusionsModal(false)}
          productId={id}
          productName={name}
        />
      )}
    </>
  );
};
export default ProductListItem;
